import { action } from "typesafe-actions";
import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { api } from "../../api/api";
import { ICompanyOutlet, ICustomisedColumn } from "./companyOutlet.types";
import {
	saveLoaderCompleted,
	saveLoaderProgress,
	showMessage,
} from "../messages/messagesActions";
import { IQueryParams } from "../common/common.types";
import { getSearchQuery } from "../common/helpers";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";

// Action Types for Customised Column
export const FETCH_CUSTOMISED_COLUMN_PROGRESS = "FETCH_CUSTOMISED_COLUMN_PROGRESS";
export const FETCH_CUSTOMISED_COLUMN_SUCCESS = "FETCH_CUSTOMISED_COLUMN_SUCCESS";
export const FETCH_CUSTOMISED_COLUMN_FAILED = "FETCH_CUSTOMISED_COLUMN_FAILED";

// Action Types
export const FETCH_COMPANY_OUTLET_LIST_PROGRESS =
	"FETCH_COMPANY_OUTLET_LIST_PROGRESS";
export const FETCH_COMPANY_OUTLET_LIST_SUCCESS =
	"FETCH_COMPANY_OUTLET_LIST_SUCCESS";
export const FETCH_COMPANY_OUTLET_LIST_FAILED =
	"FETCH_COMPANY_OUTLET_LIST_FAILED";

export const FETCH_COMPANY_OUTLET_PROGRESS = "FETCH_COMPANY_OUTLET_PROGRESS";
export const FETCH_COMPANY_OUTLET_SUCCESS = "FETCH_COMPANY_OUTLET_SUCCESS";
export const FETCH_COMPANY_OUTLET_FAILED = "FETCH_COMPANY_OUTLET_FAILED";

// Action Creators
export const fetchCompanyOutletListProgress = () =>
	action(FETCH_COMPANY_OUTLET_LIST_PROGRESS);
export const fetchCompanyOutletListSuccess = (data: ICompanyOutlet[]) =>
	action(FETCH_COMPANY_OUTLET_LIST_SUCCESS, { data });
export const fetchCompanyOutletListFailed = () =>
	action(FETCH_COMPANY_OUTLET_LIST_FAILED);

export const fetchCompanyOutletProgress = () =>
	action(FETCH_COMPANY_OUTLET_PROGRESS);
export const fetchCompanyOutletSuccess = (data: ICompanyOutlet) =>
	action(FETCH_COMPANY_OUTLET_SUCCESS, { data });
export const fetchCompanyOutletFailed = () =>
	action(FETCH_COMPANY_OUTLET_FAILED);

// Async Actions
export const fetchCompanyOutletListAsync =
	(
		pageNumber: number,
		rowsInPerPage: number,
		limit: number,
		status: string,
		date?: IDataTableV2DateState["dates"],
		searchValue?: IDatatableV2AdvancedSearchFilter,

		searchByName?: string
	): ThunkAction<void, IStoreState, {}, AnyAction> =>
		async (dispatch, getState) => {
			try {
				let searchQuery = "";

				if ((searchValue?.length ?? 0) > 0 || searchByName) {
					const advanceFilter = [...(searchValue || [])]; // Ensure it's always an array

					if (searchByName) {
						advanceFilter.push({
							column: ["company_outlet_name"],
							value: searchByName,
							logicalOperator: "AND",
							operator: "CONTAINS",
						});
					}

					searchQuery += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
				}

				if (date) {
					searchQuery += `&from_date=${date.fromDate}&to_date=${date.toDate}`;
				}

				dispatch(fetchCompanyOutletListProgress());
				const res = await api.get(
					`/dataManagement/get-company-outlet?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}` +
					searchQuery
				);
				const data: ICompanyOutlet[] = res.data.data;
				const totalRecords = res.data.totalRecords;

				dispatch(fetchCompanyOutletListSuccess(data));
			} catch (err: any) {
				dispatch(fetchCompanyOutletListFailed());
				dispatch(
					showMessage({
						type: "error",
						message: err.response.data.message,
						displayAs: "snackbar",
					})
				);
			}
		};

export const fetchCompanyOutletAsync =
	(companyOutletUuid: any): ThunkAction<void, IStoreState, {}, AnyAction> =>
		async (dispatch) => {
			try {
				dispatch(fetchCompanyOutletProgress());
				dispatch(saveLoaderProgress());
				const res = await api.get(
					`/dataManagement/get-company-outlet?company_outlet_uuid=${companyOutletUuid}`
				);
				const data = res.data.data;
				if (data.length > 0) {
					dispatch(fetchCompanyOutletSuccess(data[0]));
				} else {
					dispatch(fetchCompanyOutletFailed());
				}
			} catch (err: any) {
				dispatch(fetchCompanyOutletFailed());
				dispatch(
					showMessage({
						type: "error",
						message: err.response.data.message,
						displayAs: "snackbar",
					})
				);
			} finally {
				dispatch(saveLoaderCompleted());
			}
		};

export const upsertCompanyOutletAsync =
	(
		companyOutlet: ICompanyOutlet,
		onCallback: (isSuccess?: boolean, data?: ICompanyOutlet) => void
	): ThunkAction<void, IStoreState, {}, AnyAction> =>
		async (dispatch) => {
			dispatch(saveLoaderProgress());
			try {
				const { create_ts, insert_ts, created_by_uuid, ...rest } = companyOutlet;
				const res = await api.post("/dataManagement/upsert-company-outlet", rest);
				dispatch(
					showMessage({
						type: "success",
						message: res.data.message,
						displayAs: "snackbar",
					})
				);
				const data = res.data.data;
				onCallback(true, data);
			} catch (err: any) {
				onCallback(false);
				dispatch(
					showMessage({
						type: "error",
						message: err.response.data.message,
						displayAs: "snackbar",
					})
				);
			} finally {
				dispatch(saveLoaderCompleted());
			}
		};

export const upsertCustomisedColumnAsync =
	(
		columnConfig: ICustomisedColumn,
		onCallback: (isSuccess?: boolean) => void
	): ThunkAction<void, IStoreState, {}, AnyAction> =>
		async (dispatch) => {
			dispatch(saveLoaderProgress());

			const { create_ts, insert_ts, created_by_uuid, ...rest } = columnConfig
			try {
				const res = await api.post("/dataManagement/upsert-customised-column", columnConfig);
				dispatch(
					showMessage({
						type: "success",
						message: res.data.message,
						displayAs: "snackbar",
					})
				);
				onCallback(true);
			} catch (err: any) {
				onCallback(false);
				dispatch(
					showMessage({
						type: "error",
						message: err.response.data.message,
						displayAs: "snackbar",
					})
				);
			} finally {
				dispatch(saveLoaderCompleted());
			}
		};

// Action Creators for Customised Column
export const fetchCustomisedColumnProgress = () =>
	action(FETCH_CUSTOMISED_COLUMN_PROGRESS);
export const fetchCustomisedColumnSuccess = (data: ICompanyOutlet[]) =>
	action(FETCH_CUSTOMISED_COLUMN_SUCCESS, { data });
export const fetchCustomisedColumnFailed = () =>
	action(FETCH_CUSTOMISED_COLUMN_FAILED);

// Async Action for fetching Customised Column
export const fetchCustomisedColumnAsync =
	(company_outlet_uuid: string, module_name: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
		async (dispatch) => {
			try {
				dispatch(fetchCustomisedColumnProgress());
				dispatch(saveLoaderProgress());
				const res = await api.get(
					`/dataManagement/get-customised-column?company_outlet_uuid=${company_outlet_uuid}&columns=module_name&value=${module_name}`
				);
				const data = res.data.data;
				dispatch(fetchCustomisedColumnSuccess(data));
			} catch (err: any) {
				dispatch(fetchCustomisedColumnFailed());
				dispatch(
					showMessage({
						type: "error",
						message: err.response.data.message,
						displayAs: "snackbar",
					})
				);
			} finally {
				dispatch(saveLoaderCompleted());
			}
		};

export const CLEAR_COMPANY_OUTLET_STATE = "CLEAR_COMPANY_OUTLET_STATE";
export const CLEAR_COMPANY_OUTLET = "CLEAR_COMPANY_OUTLET";

export const clearCompanyOutletState = () => action(CLEAR_COMPANY_OUTLET_STATE);
export const clearCompanyOutlet = () => action(CLEAR_COMPANY_OUTLET);
